import { consumePostHogIdentitySyncedForPage, syncPostHogIdentity } from '@hookdeck/analytics';
import { createContext, PropsWithChildren, useContext, useLayoutEffect, useMemo } from 'react';
import useSWR from 'swr';

import { User } from '../../../../../typings/User.interface';
import APIMethodKeys from '../../client/APIMethodKeys';
import { HookdeckAPIError } from '../../client/Hookdeck';
import Icon from '../common/base/Icon';
import FullscreenLayout from '../layouts/FullscreenLayout';
import { GlobalContext } from './GlobalContext';

export type UserContext = {
  user: User | null;
  mutateUser: ((user: User) => void) | null;
  user_error: HookdeckAPIError | null | undefined;
  loading_user: boolean;
};

export const UserContext = createContext<UserContext>({
  mutateUser: null,
  user: null,
  user_error: null,
  loading_user: true,
});

export const UserContextProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { HookdeckAPI, configs } = useContext(GlobalContext);
  const {
    data: user,
    error: user_error,
    isLoading: is_loading,
    mutate,
  } = useSWR(APIMethodKeys.session.me(), () => HookdeckAPI.session.me(), {
    shouldRetryOnError: false,
  });

  // SWR sets `isLoading` back to true on every revalidation while there is no
  // cached data — permanently the case on unauthenticated pages where the
  // session request 401s. Gating the fullscreen loader on it alone unmounts the
  // whole page on window refocus (revalidateOnFocus), wiping in-progress state
  // such as the /signup form. Only treat the initial, unresolved fetch as loading.
  const loading_user = is_loading && user_error === undefined;

  useLayoutEffect(() => {
    if (user) {
      const posthog = (window as { posthog?: Parameters<typeof syncPostHogIdentity>[0] }).posthog;
      if (posthog && !consumePostHogIdentitySyncedForPage(user.id)) {
        syncPostHogIdentity(posthog, user, {
          createdAt: user.created_at,
          env: configs.ENV,
        });
      }

      HookdeckAPI.track.event('Started Session');

      if ((window as any).$crisp) {
        (window as any).$crisp.push(['set', 'user:email', [user.email]]);
        (window as any).$crisp.push(['set', 'user:nickname', [user.name]]);
      }
    }
  }, [user, configs.ENV]);

  const context: UserContext = useMemo(
    () => ({
      user: user || null,
      mutateUser: mutate,
      user_error,
      loading_user,
    }),
    [user, user_error, mutate, loading_user],
  );

  if (loading_user) {
    return (
      <FullscreenLayout center no_decorations>
        <Icon icon="loading" muted />
      </FullscreenLayout>
    );
  }

  return <UserContext.Provider value={context}>{children}</UserContext.Provider>;
};

export const withUserContext = (prop_keys: string[]) =>
  function <C extends React.ComponentClass>(Component: C): C {
    const getProps = (context) =>
      Object.keys(context)
        .filter((key) => prop_keys.includes(key))
        .reduce((filtered_props, key) => ({ ...filtered_props, [key]: context[key] }), {});
    const UserContextConsumer = (props) => {
      return (
        <UserContext.Consumer>
          {(context) => <Component {...{ ...getProps(context), ...props }} />}
        </UserContext.Consumer>
      );
    };
    return UserContextConsumer as any as C;
  };
